import { ExchangeRateTab } from "@/modules/exchangeRate/common/enum";
import { currencyOptions, getExchangeRateTabItems } from "@/modules/exchangeRate/helpers";
import type { ICurrencyExchangeCalculation } from "@/modules/transaction/common/types";
import { useLazyGetExchangeRatePersonalizedQuery } from "@/modules/transaction/services";
import { CURRENCY } from "@/shared/common/constants";
import { CloudflareAsset, Currencies } from "@/shared/common/enums";
import type { ICurrency } from "@/shared/common/types";
import { CloudFlareImage, Select } from "@/shared/components";
import { cleanupAmount, isValidAmountInput } from "@/shared/helpers";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Tab } from "@ab.uitools/ui-library/components/Tab";
import { Text } from "@ab.uitools/ui-library/components/Text";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { CurrencySelectOption } from "./CurrencySelectOption";
import { CurrencySelectSingleValue } from "./CurrencySelectSingleValue";

export const ExchangeRateCalculator = () => {
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = useState<string>(ExchangeRateTab.NonCash);
  const [sellCurrency, setSellCurrency] = useState<Currencies>(Currencies.USD);
  const [buyCurrency, setBuyCurrency] = useState<Currencies>(Currencies.AMD);
  const [haveAmount, setHaveAmount] = useState<string>();
  const [wantAmount, setWantAmount] = useState<string>();

  const [getPersonalizedRate, { data: rateInfo }] = useLazyGetExchangeRatePersonalizedQuery();

  const getCalculatedAmount = ({
    amount,
    isHaveAmount,
  }: ICurrencyExchangeCalculation) => {
    const rate = `${rateInfo?.rate}`;
    if (!rate) return;
    const sameCurrency = rateInfo?.currency === (isHaveAmount ? sellCurrency : buyCurrency);
    return sameCurrency ?
      +cleanupAmount(amount) / +cleanupAmount(rate) :
      +cleanupAmount(amount) * +cleanupAmount(rate);
  };

  const handleChangeHaveAmount = (val: string) => {
    setHaveAmount(val);
    const amount = getCalculatedAmount({
      amount: val,
      isHaveAmount: true,
    });
    setWantAmount(`${amount?.toFixed(2)}`);
  };

  const handleChangeWantAmount = (val: string) => {
    setWantAmount(val);
    const amount = getCalculatedAmount({ amount: val });
    setHaveAmount(`${amount?.toFixed(2)}`);
  };

  const handleSwapCurrencies = () => {
    const currentSellCurrency = sellCurrency;
    setSellCurrency(buyCurrency);
    setBuyCurrency(currentSellCurrency);
  };

  const haveCurrencyInfo = rateInfo?.currency === sellCurrency ? buyCurrency : sellCurrency;
  const wantCurrencyInfo = rateInfo?.currency === sellCurrency ? sellCurrency : buyCurrency;

  const buyCurrencyOptions = useMemo(() => {
    return currencyOptions.filter((item) => item.value !== sellCurrency);
  }, [sellCurrency]);

  const sellCurrencyOptions = useMemo(() => {
    return currencyOptions.filter((item) => item.value !== buyCurrency);
  }, [buyCurrency]);

  const rateHelperText = useMemo(() => {
    if (!rateInfo?.rate) return "";
    return (
      `1 ${(CURRENCY?.[haveCurrencyInfo] as ICurrency).symbol}
        = ${haveCurrencyInfo === wantCurrencyInfo ?
        1 : rateInfo?.rate} ${(CURRENCY?.[wantCurrencyInfo] as ICurrency).symbol}`
    );
  }, [haveCurrencyInfo, wantCurrencyInfo, rateInfo]);

  useEffect(() => {
    if (sellCurrency && buyCurrency) {
      getPersonalizedRate({
        sellCurrency,
        buyCurrency,
      });
    }
  }, [sellCurrency, buyCurrency]);

  useEffect(() => {
    if (rateInfo?.rate && haveAmount) {
      const amount = getCalculatedAmount({
        amount: haveAmount,
        isHaveAmount: true,
      });
      setWantAmount(amount?.toFixed(2));
    }
  }, [rateInfo]);

  return (
    <div
      className="widget exchange-rate-calculator">
      <div className="exchange-rate-calculator-body">
        <div className="flexbox full-width justify-content--between align-items--center">
          <div className="full-width flexbox align-items--center pt-12">
            <Text className="truncate" weight="bold" size="large">
              {t("exchangeRate.calculator")}
            </Text>
          </div>
        </div>
        <div className="full-width tabs-section pb-20">
          <Tab
            size="medium"
            color="light"
            tabItems={getExchangeRateTabItems({ t })}
            selectedValue={selectedValue}
            onSelect={(value) => setSelectedValue(value as string)}
          />
        </div>
        <div className="full-width mt-16 gap-4 exchange-rate-calculator-currencies">
          <Select
            className="currency-select"
            options={sellCurrencyOptions}
            isSearchable={false}
            setSelectedValue={(value) => setSellCurrency(value as Currencies)}
            selectedValue={sellCurrency}
            components={{
              Option: CurrencySelectOption,
              SingleValue: CurrencySelectSingleValue,
            }}
          />
          <div
            className="flexbox flex-direction--column align-items--center justify-content--between px-8">
            <CloudFlareImage
              className="cursor-pointer"
              onClick={handleSwapCurrencies}
              path={CloudflareAsset.CurrencyExchangeArrows}
            />
            <Text className="text-nowrap pt-2" type="tertiary" size="small">
              {rateHelperText}
            </Text>
          </div>
          <Select
            className="currency-select"
            options={buyCurrencyOptions}
            isSearchable={false}
            setSelectedValue={(value) => setBuyCurrency(value as Currencies)}
            selectedValue={buyCurrency}
            components={{
              Option: CurrencySelectOption,
              SingleValue: CurrencySelectSingleValue,
            }}
          />
        </div>
        <div className="flexbox gap-16 mt-16 pb-8">
          <Input
            label={t("exchangeRate.iHave")}
            type="numeric"
            className="exchange-rate-calculator-input"
            currentValue={haveAmount}
            handleChange={(_, val) => handleChangeHaveAmount(val)}
            isAllowed={({ formattedValue }) => isValidAmountInput(formattedValue)}
            rightIconProps={{
              Component: CURRENCY?.[sellCurrency]?.symbolIcon || "",
            }}
          />
          <Input
            label={t("exchangeRate.iWant")}
            currentValue={wantAmount}
            type="numeric"
            className="exchange-rate-calculator-input"
            handleChange={(_, val) => handleChangeWantAmount(val)}
            isAllowed={({ formattedValue }) => isValidAmountInput(formattedValue)}
            rightIconProps={{
              Component: CURRENCY?.[buyCurrency]?.symbolIcon || "",
            }}
          />
        </div>
      </div>
    </div>
  );
};
